<template>
  <div class="index">
    <div class="index" v-if="showPage">
      <alert
        :alertContent="alertContent"
        @alertButtonClick="alertButtonClick"
        v-if="shouldShowAlert"
      ></alert>

      <banner
        :isUserScoring="isUserScoringTheEvent"
        :hasEntered="entriedCurrentEvent"
        :url="this.e.current.headerImageUrl"
        :closed="eventClosed"
        :beginAt="new Date(this.e.current.beginAt)"
        :endAt="new Date(this.e.current.endAt)"
        :countdownBackgroundBaseColor="this.descriptionBgColor"
        :bannerFontColor="this.fontColor"
      ></banner>

      <tab
        :results="this.results"
        :event="this.e.current"
        :groupId="this.groupId"
        :next="this.next"
        :descriptionContent="this.descriptionContent"
        :descriptionBgColor="this.descriptionBgColor"
        :descriptionBgImage="this.descriptionBgImage"
        :descriptionBgRepeat="this.descriptionBgRepeat"
        :tabFontColor="this.fontColor"
        :tabId="this.queryParams.tb"
        :isEntried="entriedCurrentEvent"
        :myResult="currentUserResult"
        :targetResult="viewingUserResult"
        :totalNumOfParticipants="totalNumOfParticipants"
        :groupIdToNumOfParticipants="groupIdToNumOfParticipants"
        @loadResult="fetchResults()"
      ></tab>

      <div v-bind:class="this.overlay_class">
        <event-entry-button
          v-if="shouldShowEntryButton"
          :class="entryButtonClassName"
          :event="currentBroadcasterEvent"
          :entry="currentBroadcasterEventEntry"
          :isRankEnabled="isRankEnabled"
          :isRankConditionSatisfied="isRankConditionSatisfied"
          @clickEntry="tapEntryButton"
          @clickPreEntry="tapPreEntryButton"
          @clickSwitchingScoringEvent="tapSwitchingScoringEventButton"
          @clickOptin="tapOptinButton"
          @clickCancelPreEntry="tapCancelPreEntryButton"
        ></event-entry-button>

        <!-- プレエントリー済みならキャンセル -->
      </div>
    </div>
  </div>
</template>

<script>
import APIRequest from '@/utils/APIRequest'
import Banner from '@/components/Banner.vue'
import Tab from '@/components/Tab.vue'
import EventEntryButton from '@/pages/events/components/EventEntryButton.vue'
import Alert from '@/components/Alert.vue'
import AlertFactory from '@/utils/AlertFactory'
import { getToken } from '@/utils/token'
import { EventTracker } from '@/utils/event_tracker'
import { redirectToTrackerURLIfNeeded } from '@/utils/adjust_redirector'
import { LanguageQueryMixin } from '@/mixins/LanguageQuery'
import { getLocationName } from '@/utils/time'
import { selectIfConflicts } from '@/services/event'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'
import { BroadcasterEvent } from './models/BroadcasterEvent'
import { BroadcasterEventEntry } from './models/BroadcasterEventEntry'
import dayjs from 'dayjs'
import { CustomTraceProvider } from '@/utils/custom_trace'

export default {
  components: {
    Banner,
    Tab,
    Alert,
    EventEntryButton,
  },
  mixins: [LanguageQueryMixin],
  title: 'イベント',
  name: 'Event',
  data: function () {
    return {
      results: [],
      next: 1,
      eventId: undefined,
      groupId: undefined,
      queryParams: {
        st: this.$route.query.st,
        tp: this.$route.query.tp,
        tb: this.$route.query.tb,
        vi: this.$route.query.vi,
        from: this.$route.query.from,
        mediaId: this.$route.query.media_id,
      },
      e: {
        current: undefined,
      },
      title: '',
      entries: undefined,
      shouldShowAlert: false,
      alertContent: {},
      descriptionContent: '',
      descriptionBgColor: '',
      descriptionBgImage: '',
      descriptionBgRepeat: '',
      fontColor: '',
      viewingUserResult: undefined,
      overlay_class: 'overlay',
      avatar: undefined,
      token: undefined,
      user: null,
      currentUserResult: null,
      processing: false,
      isFirstAccess: true,
      isOpenGroupInfo: false,
      isProcessingEntry: false,
      eventConstraints: null,
      rankInfo: undefined, // セッショントークンで見ているユーザーのランク情報
      isUserScoringTheEvent: undefined, // ユーザーがこのイベントで現在スコア加算中かどうか
      totalNumOfParticipants: 0, // イベントの参加者数
      groupIdToNumOfParticipants: undefined, // グループIDごとの参加者数(グループイベントじゃない場合はundefined)
      isTouchMoveListenerAdded: false, // イベントリスナーの重複登録防止フラグ
    }
  },
  computed: {
    showPage: function () {
      return this.e.current !== undefined
    },
    eventClosed: function () {
      return this.e.current.status == 3 || this.e.current.status == 4
    },
    eventAccepting: function () {
      return this.e.current.status == 0 || this.e.current.status == 1
    },
    entriedCurrentEvent: function () {
      return this.currentBroadcasterEventEntry !== undefined
    },
    currentBroadcasterEventEntry() {
      if (!this.e.current || !this.entries) {
        return undefined
      }
      const entry = this.entries.find(
        (entry) => entry.parentId == this.e.current.parentId
      )
      if (entry === undefined) {
        return undefined
      }
      return BroadcasterEventEntry.fromObject(entry)
    },
    currentBroadcasterEvent() {
      if (!this.e.current) {
        return undefined
      }
      return BroadcasterEvent.fromObject(this.e.current)
    },
    shouldShowEntryButton: function () {
      return (
        this.getToken() !== undefined &&
        this.eventAccepting &&
        !this.isUserScoringTheEvent
      )
    },
    targetUserResult() {
      return this.viewingUserResult
    },
    myResult() {
      if (
        this.entriedCurrentEvent &&
        this.e.current.status !== 0 &&
        !!this.currentUserResult
      ) {
        return this.currentUserResult
      }
      return null
    },
    hasAvatar: function () {
      return this.avatar !== undefined
    },
    isIOS() {
      const ua = window.navigator.userAgent
      return ua.indexOf('iPhone') > 0 || ua.indexOf('iPad') > 0
    },
    isRankEnabled() {
      return this.rankInfo?.isEnabled
    },
    isRankConditionSatisfied() {
      if (!this.currentBroadcasterEvent?.isUserRankEvent()) {
        return false
      }
      if (!this.rankInfo) {
        return false
      }
      const rankValue = this.rankInfo.rank
      return this.eventConstraints.userRank.UserRanks?.includes(rankValue)
    },
    entryButtonClassName() {
      // 実際のversionが何であるかは意識しなくて良い
      // なぜならこのversionカラムを渡す実装になっているバージョンのiosこそがCSSの出しわけをしたい対象だから
      const version = this.$route.query.version
      return version ? 'overlay__entryButton_new_ios' : 'overlay__entryButton'
    },
  },
  watch: {
    shouldShowAlert: function (val) {
      this.allowScrolling(!val)
    },
    entriedCurrentEvent: function (val) {
      this.fetchCurrentUserResult()
    },
  },
  beforeDestroy() {
    if (this.isTouchMoveListenerAdded) {
      document.removeEventListener('touchmove', this.handleTouchMove)
      this.isTouchMoveListenerAdded = false
    }
  },
  created() {
    // パフォーマンス調査のためのトレース開始
    const traceName = 'event-page:created'
    CustomTraceProvider.startTrace(traceName)

    redirectToTrackerURLIfNeeded()

    this.token = this.getToken()
    this.fetchUserEntryResult()
    this.fetchUserAvatar()
    this.allowScrolling(true)
    this.fetchStreamerUser().then(() => {
      this.fetchCurrentUserResult()
    })
    this.version = this.$route.query.version

    let ua = window.navigator.userAgent
    const isIPad =
      /iPad/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isIPhone = /iPhone OS/.test(ua)

    if (isIPhone || isIPad) {
      this.overlay_class = 'overlay__iOS_SM'
    }

    // トレース終了
    CustomTraceProvider.stopTrace(traceName)
  },
  methods: {
    handleTouchMove: function (e) {
      e.preventDefault()
    },
    getToken() {
      return getToken()
    },
    allowScrolling: function (val) {
      if (val) {
        document.body.className = 'body__scrolling'
        if (this.isTouchMoveListenerAdded) {
          document.removeEventListener('touchmove', this.handleTouchMove)
          this.isTouchMoveListenerAdded = false
        }
      } else {
        document.body.className = 'body__nonscrolling'
        if (!this.isTouchMoveListenerAdded) {
          document.addEventListener('touchmove', this.handleTouchMove, { passive: false })
          this.isTouchMoveListenerAdded = true
        }
      }
    },
    tapEntryButton: function () {
      // firebase analyticsで応募ボタンタップイベントをトラッキング
      EventTracker.default.trackTapEventEntryButton(this.eventId)

      const onEntryDecide = () => {
        this.entry((isSuccess, message) => {
          this.alertContent = AlertFactory.makeCompletedEntryContent(
            () => {
              this.shouldShowAlert = false
              location.reload(true)
            },
            isSuccess,
            message
          )
        })
      }
      const hideAlert = () => (this.shouldShowAlert = false)

      this.shouldShowAlert = true

      // アバターを持っていなかったら警告
      if (!this.hasAvatar) {
        this.alertContent = AlertFactory.makeHasNotAvatarEntryContent(hideAlert)
        return
      }

      const conflictedEntries = selectIfConflicts(
        this.entries,
        new Date().toISOString(),
        this.e.current.beginAt,
        this.e.current.endAt
      )
      // すでにスコア加算を開始しているイベントがあったら警告
      const existsOtherScoringEvent = conflictedEntries.some((e) => {
        const now = dayjs()
        const eventBeginAt = dayjs(e.event.beginAt)
        return now.unix() >= eventBeginAt.unix()
      })
      if (existsOtherScoringEvent) {
        this.alertContent = AlertFactory.makeOtherScoringEventContent(
          onEntryDecide,
          hideAlert
        )
        return
      }

      // エントリーできるので確認用のダイアログを出す
      this.alertContent = AlertFactory.makeEntryContent(
        onEntryDecide,
        hideAlert
      )
    },
    tapPreEntryButton: function () {
      // firebase analyticsで応募ボタンタップイベントをトラッキング
      EventTracker.default.trackTapEventEntryButton(this.eventId)

      const onPreEntryDecide = () => {
        this.entry((isSuccess) => {
          this.alertContent = AlertFactory.makeCompletedPreEntryContent(() => {
            this.shouldShowAlert = false
            location.reload(true)
          }, isSuccess)
        })
      }
      const hideAlert = () => (this.shouldShowAlert = false)

      this.shouldShowAlert = true

      // アバターを持っていなかったら警告
      if (!this.hasAvatar) {
        this.alertContent = AlertFactory.makeHasNotAvatarEntryContent(hideAlert)
        return
      }

      const conflictedEntries = selectIfConflicts(
        this.entries,
        new Date().toISOString(),
        this.e.current.beginAt,
        this.e.current.endAt
      )
      // すでにプレエントリー済みのイベントがあったら警告
      const otherPreEnteredEvents = conflictedEntries
        .map((e) => e.event)
        .filter((e) => {
          const now = dayjs()
          const eventBeginAt = dayjs(e.beginAt)
          return now.unix() < eventBeginAt.unix()
        })
      if (otherPreEnteredEvents.length > 0) {
        this.alertContent = AlertFactory.makeOverridePreEntryContent(
          otherPreEnteredEvents,
          onPreEntryDecide,
          hideAlert
        )
        return
      }

      // プレエントリーできるので確認用のダイアログを出す
      this.alertContent = AlertFactory.makePreEntryContent(
        onPreEntryDecide,
        hideAlert
      )
    },
    tapSwitchingScoringEventButton: function () {
      // firebase analyticsで応募ボタンタップイベントをトラッキング
      EventTracker.default.trackTapEventEntryButton(this.eventId)

      const onSwitchingScoringEventDecide = () => {
        this.entry((isSuccess, message) => {
          this.alertContent =
            AlertFactory.makeSwitchingScoringEventCompletedContent(
              () => {
                this.shouldShowAlert = false
                location.reload(true)
              },
              isSuccess,
              message
            )
        })
      }
      const hideAlert = () => (this.shouldShowAlert = false)

      this.shouldShowAlert = true

      // スコア加算対象確認用のダイアログを出す
      this.alertContent = AlertFactory.makeSwitchingScoringEventContent(
        onSwitchingScoringEventDecide,
        hideAlert
      )
    },
    tapCancelPreEntryButton: function () {
      const onCancelPreEntryDecide = () => {
        this.cancelPreEntry((isSuccess) => {
          this.alertContent = AlertFactory.makeCompletedCancelPreEntryContent(
            () => {
              this.shouldShowAlert = false
              location.reload(true)
            },
            isSuccess
          )
        })
      }
      const hideAlert = () => (this.shouldShowAlert = false)

      this.shouldShowAlert = true

      // プレエントリーできるので確認用のダイアログを出す
      this.alertContent = AlertFactory.makeConfirmCancelPreEntryContent(
        onCancelPreEntryDecide,
        hideAlert
      )
    },
    tapOptinButton: function () {
      const nativeInterface = defaultNativeInterfaceClient()
      if (nativeInterface.client !== Client.native) {
        try {
          nativeInterface.sendEvent('rankOptIn', 'show', {})
        } catch (e) {}
      }
    },
    alertButtonClick: function (index) {
      this.alertContent.alertItems[index].action()
    },
    fetchUserEntryResult: async function () {
      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/event/broadcaster/get',
          {
            eventId: this.$route.params.eventId.toString(),
          },
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )
      const event = response.broadcasterEvent
      this.eventId = event.parentId

      const now = new Date().toISOString()
      this.isOpenGroupInfo = now > event.beginAt
      if (this.isOpenGroupInfo) {
        this.groupId = event.groupId
      } else {
        this.groupId = 0
      }

      // イベント情報取得 -> UserResultとEntryEventを取得
      this.fetchViewingUserResult()
      this.fetchEntryEvents().then(() => {
        // ランキングリザルト取得
        this.fetchResults()
      })
    },
    fetchEntryEvents: async function () {
      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/event/broadcaster/list',
          {},
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )
      const events = response.broadcasterEvents
      const entries = response.broadcasterEventEntries || []
      this.entries = entries.map((entry) => {
        const f = events.find((e) => e.eventId == entry.parentId)
        entry.event = f
        return entry
      })

      if (this.entries.length !== 0) {
        const entry = this.entries.find(
          (entry) => entry.parentId == this.eventId
        )
        if (entry !== undefined) {
          if (this.queryParams.vi === undefined) {
            // 配信画面からのアクセス以外ではエントリーしているグループIDをセット
            if (this.isOpenGroupInfo) {
              this.groupId = entry.groupId
            }
          }
        }
      }
    },
    fetchUserAvatar: async function () {
      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/avatar/list',
          {},
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )
      if (response.avatars && response.avatars.length > 0) {
        this.avatar = response.avatars
      }
    },
    async fetchStreamerUser() {
      const request = new APIRequest(
        this.$config.appGatewayServerUrl,
        '/streamer/owner/get',
        {
          isLatestCounting: false,
        },
        { Authorization: `Bearer ${this.getToken()}` }
      )
      const response = await this.$loader.post(request)
      this.user = response.streamer || null
      this.rankInfo = response.rankInfo || null
    },
    fetchViewingUserResult: async function () {
      if (this.queryParams.vi === undefined) {
        return
      }
      const result = await this.fetchResultByVliveId(this.queryParams.vi)
      this.viewingUserResult = result
    },
    async fetchCurrentUserResult() {
      if (!this.user || !this.user.vliveId || !this.entriedCurrentEvent) return
      const result = await this.fetchResultByVliveId(this.user.vliveId)
      this.currentUserResult = result
    },
    async fetchResultByVliveId(vliveId) {
      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/event/broadcaster/result/get',
          {
            eventId: this.eventId.toString(),
            vliveId: vliveId,
          },
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )
      return response.broadcasterEventResult
    },
    fetchResults: async function () {
      if (this.next == 0) {
        // nextが0になったら終わり
        return
      }
      if (this.processing) {
        return
      }
      if (!this.isFirstAccess) {
        // グループタブ切り替えをトラッキング
        EventTracker.default.trackSelectGroupEventButton(
          this.eventId,
          Number(this.eventId) + Number(this.groupId)
        )
      }

      this.processing = true
      const targetId = Number(this.eventId) + Number(this.groupId)
      const ignorePeriod =
        this.$route.query.ignore_period !== null &&
        this.$route.query.ignore_period == 'true'
      const locationName = getLocationName()
      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/event/broadcaster/result/list',
          {
            eventId: targetId.toString(),
            page: this.next.toString(),
            ignorePeriod: ignorePeriod,
            locationName: locationName,
          },
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )

      const results = response.broadcasterEventResults
      const page = response.pagination
      const event = response.broadcasterEvent
      const totalNumOfParticipants = response.totalNumOfParticipants

      if (page.Current == 1) {
        this.results = results
      } else {
        this.results = this.results.concat(results)
      }

      this.e.current = event
      this.title = event.title
      this.descriptionContent = event.description
      this.next = page.Next
      this.processing = false
      this.isFirstAccess = false
      this.eventConstraints = event.constraints
      this.totalNumOfParticipants = totalNumOfParticipants
      this.groupIdToNumOfParticipants = response.groupIdToNumOfParticipants

      this.isUserScoringTheEvent = response.isUserScoringTheEvent

      // デザイン設定
      if (event.pageDesign === null) {
        this.fontColor = '#0f0f0f'
        this.descriptionBgColor = '#F8F8F8'
        this.descriptionBgImage = 'none'
        this.descriptionBgRepeat = 'no-repeat'
      } else {
        // 文字色(0:黒色 1:白色 2:水色)
        if (event.pageDesign.font_color_id === 0) {
          this.fontColor = '#0f0f0f'
        } else if (event.pageDesign.font_color_id === 1) {
          this.fontColor = '#FFFFFF'
        } else {
          this.fontColor = '#188dff'
        }
        // 背景色
        if (!event.pageDesign.bg_color) {
          this.descriptionBgColor = '#F8F8F8'
        } else {
          this.descriptionBgColor = event.pageDesign.bg_color
        }

        // 背景画像(0:なし 1〜10:固定画像 11:指定画像)
        if (event.pageDesign.image_id === 0) {
          this.descriptionBgImage = 'none'
          this.descriptionBgRepeat = 'no-repeat'
        } else if (event.pageDesign.image_id === 11) {
          this.descriptionBgImage = 'url("' + event.pageImageUrl + '")'
          this.descriptionBgRepeat = 'repeat'
        } else {
          const fileName = String(event.pageDesign.image_id).padStart(4, '0')
          this.descriptionBgImage =
            'url("/img/event_page/bg_' + fileName + '.png")'
          this.descriptionBgRepeat = 'repeat'
        }
      }
    },
    entry: async function (completion, message) {
      // 連打対策
      if (this.isProcessingEntry) {
        return
      }
      this.isProcessingEntry = true

      const response = await this.$loader.post(
        new APIRequest(
          this.$config.appGatewayServerUrl,
          '/event/broadcaster/enter',
          {
            eventId: this.eventId.toString(),
            from: this.queryParams.from,
            mediaId: this.queryParams.mediaId,
          },
          {
            Authorization: `Bearer ${this.getToken()}`,
          }
        )
      )

      if (response.errorCode) {
        completion(false, response.message)
      } else {
        completion(true)
      }
      this.isProcessingEntry = false
    },
    cancelPreEntry: async function (completion, message) {
      // 連打対策(エントリーと同時に処理を動かしたくないのでisProcessingEntryを使う)
      if (this.isProcessingEntry) {
        return
      }
      if (!this.currentBroadcasterEventEntry) {
        return
      }

      this.isProcessingEntry = true

      try {
        const response = await this.$loader.post(
          new APIRequest(
            this.$config.appGatewayServerUrl,
            '/event/broadcaster/pre_entry/cancel',
            {
              eventId: this.currentBroadcasterEventEntry.eventId,
            },
            {
              Authorization: `Bearer ${this.getToken()}`,
            }
          )
        )

        if (response.errorCode) {
          completion(false, response.message)
        } else {
          completion(true)
        }
      } catch (e) {
        completion(false, e)
      } finally {
        this.isProcessingEntry = false
      }
    },
  },
}
</script>

<style scoped>
#tab {
  padding: 0 0 var(--safe-area-inset-bottom, 0px) 0;
}

.index {
  height: 100%;
  min-height: 850px;
}

.overlay {
  position: fixed;
  bottom: 0;
  width: 100%;
  pointer-events: none; /* タッチイベントを背面に通す */
}
.overlay__iOS_SM {
  position: fixed;
  bottom: var(--safe-area-inset-bottom, 0px);
  width: 100%;
  pointer-events: none; /* タッチイベントを背面に通す */
}

.overlay__entryButton {
  display: block;
  margin: 0 auto 80px;
  max-width: 95%;
  pointer-events: auto; /* ボタン自体はタッチ可能にする */
}

.overlay__entryButton_new_ios {
  display: block;
  margin: 0 auto 16pt;
  max-width: 95%;
  pointer-events: auto; /* ボタン自体はタッチ可能にする */
}

.overlay__viewingUser {
  width: 100%;
  height: 93px;
  background-color: #188dff;
}

.overlay__currentUser__android {
  width: 100%;
  height: 73px;
  background-color: #188dff;
}

.overlay__currentUser__iOS {
  width: 100%;
  height: 133px;
  background-color: #188dff;
}
</style>
